import {
  LicenseSearchFilterEnum,
  type SearchParameters,
} from '../searchParameters';
import { PreferenceScopeEnum, scopeRememberedFor } from '../searchPreferences';
import { AvailableParameters } from './availableParameters';
import type { SearchParameterSiteExclusions } from './siteExclusions';

/**
 * The licenses each `family × assettype` context offers.
 *
 * A context absent from this table offers no license filter at all, and a
 * search in one has its license cleared rather than narrowed. Three contexts
 * are absent and each for its own reason: an editorial search has no licenses
 * to choose between, a video-template search is not licensed this way, and a
 * creative search spanning image *and* film offers no single license that
 * holds across both — `rm` is image-only and `rr` is film-only, so the
 * combination the panel would have to offer does not exist.
 */
const AVAILABLE_LICENSES: Partial<
  Record<PreferenceScopeEnum, readonly LicenseSearchFilterEnum[]>
> = {
  [PreferenceScopeEnum.CREATIVE_IMAGE]: [LicenseSearchFilterEnum.ROYALITY_FREE],
  [PreferenceScopeEnum.CREATIVE_FILM]: [
    LicenseSearchFilterEnum.ROYALITY_FREE,
    LicenseSearchFilterEnum.RIGHTS_READY,
  ],
  [PreferenceScopeEnum.MIXED_IMAGE]: [
    LicenseSearchFilterEnum.RIGHTS_MANAGED,
    LicenseSearchFilterEnum.ROYALITY_FREE,
  ],
  [PreferenceScopeEnum.MIXED_FILM]: [
    LicenseSearchFilterEnum.ROYALITY_FREE,
    LicenseSearchFilterEnum.RIGHTS_READY,
  ],
};

/**
 * Which search parameters a search can carry, for one site.
 *
 * Named for parameters rather than filters because it ranges over all of them:
 * `clearUnavailable` asks of every stated parameter, including `imageurl`,
 * `page` and `phrase`, which are parameters but not filters. Filters are a
 * subset.
 *
 * The exclusions come from site config and do not change while the app runs,
 * so they are held once rather than passed to every question. A caller that
 * wants to know whether `mood` applies should not have to hold the site's
 * exclusion list to ask.
 */
export class SearchParameterAvailability {
  readonly #siteExclusions: SearchParameterSiteExclusions;

  constructor(siteExclusions: SearchParameterSiteExclusions) {
    this.#siteExclusions = siteExclusions;
  }

  /**
   * What this search can carry, worked out once.
   *
   * The mode and the parameters it leaves open are resolved here rather than
   * per question, so asking about ten parameters derives the mode once.
   */
  for(searchParameters: SearchParameters): AvailableParameters {
    return new AvailableParameters(searchParameters, this.#siteExclusions);
  }

  /**
   * Narrows the licenses a search asks for to the ones its context offers, and
   * clears the filter outright when none survive.
   *
   * Unlike `AvailableParameters#clearUnavailable`, which answers whether the
   * filter is offered at all, this answers which of its *values* the context
   * offers — a mixed-image search offers `rf` and `rm`, so `rf,rr` narrows to
   * `rf` rather than being dropped whole.
   *
   * Does nothing when the search asks for no license: an absent license is not
   * a wrong one, and filling the context's licenses here would turn the absence
   * of a choice into one.
   *
   * Returns `searchParameters` itself when every license it asks for is already
   * offered, so callers can use reference equality to skip work.
   *
   * Reads no site exclusions — a license is offered by scope rather than by
   * site — but belongs on availability because it answers the same question.
   */
  removeUnavailableLicenses(
    searchParameters: SearchParameters
  ): SearchParameters {
    const { licenses } = searchParameters;
    if (!licenses.length) return searchParameters;

    const scope = scopeRememberedFor(searchParameters);
    const available = scope ? AVAILABLE_LICENSES[scope] : undefined;
    if (!available) return searchParameters.clear('license');

    const kept = licenses.filter((license) => available.includes(license));
    if (kept.length === 0) return searchParameters.clear('license');
    if (kept.length === licenses.length) return searchParameters;

    // Back to the one token the filter is set to. The members came out of it
    // in the order a combination names them, so joining them returns a value
    // the enum holds.
    return searchParameters.set(
      'license',
      kept.join(',') as LicenseSearchFilterEnum
    );
  }
}
